import { useState, useEffect } from 'react';
import { checkIsAdmin, checkIsCustomer, UserInformation } from '../authorization/UserInformation';
import { msalInit } from '../authorization/AuthorizationProvider';
import {
    getToken,
    getActiveAccount,
    forceRefreshAccessToken,
    refreshTokensUntilDentifierRoles,
} from '../authorization/AuthorizationUtils';
import { baseUrl } from '../services/Api';

interface UserHookReturn {
    userInformation: UserInformation | null;
    loading: boolean;
    isAdmin: boolean;
    isClient: boolean;
    isBlocked: boolean;
    error: string | null;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const useUser = (): UserHookReturn => {
    const [loading, setLoading] = useState(true);
    const [userInformation, setUserInformation] = useState<UserInformation | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let retryCount = 0;
        let mounted = true;

        const finishBlocked = async () => {
            const userInfo = await UserInformation.fromTokenClaims().catch(() => null);
            if (!mounted) return;
            setUserInformation(userInfo);
            setIsBlocked(true);
            setIsAdmin(false);
            setIsClient(false);
            setLoading(false);
        };

        const initializeUserInformation = async () => {
            try {
                setError(null);

                await msalInit;

                const account = await getActiveAccount();
                if (!account) {
                    if (mounted) {
                        setLoading(false);
                    }
                    return;
                }

                const token = await getToken();
                if (!token) {
                    throw new Error('No valid authentication token found');
                }

                const apiRoot = baseUrl?.replace(/\/$/, '');
                if (apiRoot) {
                    const blockedRes = await fetch(`${apiRoot}/me/blocked`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (blockedRes.ok) {
                        const blockedBody = await blockedRes.json().catch(() => ({}));
                        if (blockedBody.blocked === true) {
                            await finishBlocked();
                            return;
                        }
                    }
                }

                try {
                    if (apiRoot) {
                        const res = await fetch(`${apiRoot}/me/ensure-customer-group`, {
                            method: 'POST',
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                            body: '{}',
                        });
                        if (res.ok) {
                            const body = await res.json().catch(() => ({} as Record<string, unknown>));
                            if (body.blocked === true) {
                                await finishBlocked();
                                return;
                            }
                            if (body.alreadyMember !== true) {
                                await refreshTokensUntilDentifierRoles();
                            } else {
                                await forceRefreshAccessToken();
                            }
                        }
                    } else {
                        await forceRefreshAccessToken();
                    }
                } catch {
                    await forceRefreshAccessToken();
                }

                const userInfo = await UserInformation.fromTokenClaims();
                if (!userInfo && mounted) {
                    throw new Error('Failed to get user information');
                }

                const [adminCheck, clientCheck] = await Promise.all([
                    checkIsAdmin(),
                    checkIsCustomer()
                ]);

                if (mounted) {
                    setUserInformation(userInfo);
                    setIsAdmin(adminCheck);
                    setIsClient(clientCheck);
                    setIsBlocked(false);

                    localStorage.setItem('userRoles', JSON.stringify({
                        isAdmin: adminCheck,
                        isClient: clientCheck
                    }));
                    setLoading(false);
                }
            } catch (error) {
                if (!mounted) return;

                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                console.error('Error in user initialization:', errorMessage);

                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    console.log(
                        "Retrying initialization (" +
                            retryCount +
                            "/" +
                            MAX_RETRIES +
                            ")..."
                    );
                    setTimeout(initializeUserInformation, RETRY_DELAY);
                    return;
                }

                setError(errorMessage);
                setUserInformation(null);
                setIsAdmin(false);
                setIsClient(false);
                setIsBlocked(false);
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        initializeUserInformation();

        return () => {
            mounted = false;
        };
    }, []);

    return { userInformation, loading, isAdmin, isClient, isBlocked, error };
};

export default useUser;
